import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Building2, Loader2, ImagePlus, CheckCircle2, Clock, XCircle } from 'lucide-react';
import {
  AdAccount,
  CreateAdAccountPayload,
  createAdAccount,
  getMyAdAccounts,
  BusinessWallet,
  RequestTopUpResult,
  getBusinessWallet,
  requestTopUp,
  TaxLookupResult,
  lookupTaxCode,
} from '../../api/ads/ad-account.api';
import { fetchUrlImages } from '../../api/posts/fetch-post.api';
import { EkycCameraModal } from '../../pages/donate-goal/ekyc-camera-modal';
import { getEkycStatus, EkycStatus } from '../../api/user/ekyc.api';
import { isIndividualAccount } from '../../utils/individual-order';
import PolicyConsentModal from '../policy/policy-consent-modal';
import { getConsentStatus } from '../../api/user/consent.api';

const fmtVnd = (n: number) => `${Number(n || 0).toLocaleString('vi-VN')}đ`;

// Ví doanh nghiệp (VND) + nạp qua chuyển khoản.
const BusinessWalletBox: React.FC<{ accountId: string; businessName: string }> = ({ accountId, businessName }) => {
  const [wallet, setWallet] = useState<BusinessWallet | null>(null);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(1000000);
  const [payerName, setPayerName] = useState('');
  const [payerAccountNo, setPayerAccountNo] = useState('');
  const [payerBank, setPayerBank] = useState('');
  const [busy, setBusy] = useState(false);
  const [instruction, setInstruction] = useState<RequestTopUpResult | null>(null);

  const load = () => getBusinessWallet(accountId).then(setWallet).catch(() => undefined);
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  const submit = async () => {
    if (amount < 100000) return toast.error('Nạp tối thiểu 100.000đ.');
    if (!payerName.trim()) return toast.error('Nhập tên chủ tài khoản ngân hàng sẽ chuyển tiền.');
    try {
      setBusy(true);
      const res = await requestTopUp(accountId, {
        amountVnd: amount,
        payerAccountName: payerName.trim(),
        payerAccountNo: payerAccountNo.trim() || undefined,
        payerBankName: payerBank.trim() || undefined,
      });
      setInstruction(res);
      if (!res.payerNameMatch) {
        toast('⚠ Tên tài khoản chuyển tiền chưa khớp tên công ty — vui lòng chuyển từ tài khoản đứng tên công ty để được duyệt nhanh.', {
          duration: 6000,
        });
      }
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Tạo yêu cầu nạp thất bại.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 rounded-lg bg-[#f6fbff] dark:bg-slate-800 p-3">
      <div className="flex items-center justify-between">
        <span className="text-sm ui-muted">💰 Số dư ví (VND)</span>
        <span className={`text-lg font-bold ${(wallet?.balanceVnd ?? 0) < 0 ? 'text-rose-600' : 'text-[#0071A6] dark:text-[#48afff]'}`}>
          {fmtVnd(wallet?.balanceVnd ?? 0)}
        </span>
      </div>
      {(wallet?.creditLimitVnd ?? 0) > 0 && (
        <div className="mt-1 space-y-0.5 text-xs">
          <div className="flex items-center justify-between ui-muted">
            <span>Hạn mức tín dụng (trả sau)</span>
            <span>{fmtVnd(wallet?.creditLimitVnd ?? 0)}</span>
          </div>
          {(wallet?.outstandingDebtVnd ?? 0) > 0 && (
            <div className="flex items-center justify-between text-rose-600 font-medium">
              <span>Đang nợ (nạp tiền để trả)</span>
              <span>{fmtVnd(wallet?.outstandingDebtVnd ?? 0)}</span>
            </div>
          )}
          <div className="flex items-center justify-between text-emerald-700 dark:text-emerald-300 font-medium">
            <span>Còn được tiêu cho quảng cáo</span>
            <span>{fmtVnd(wallet?.availableVnd ?? 0)}</span>
          </div>
        </div>
      )}
      {!open && (
        <button onClick={() => { setOpen(true); setInstruction(null); }}
          className="mt-2 rounded-md bg-[#0071A6] text-white text-sm px-3 py-1.5 hover:brightness-110">
          Nạp tiền
        </button>
      )}
      {open && !instruction && (
        <div className="mt-2 space-y-2">
          <label className="block text-xs ui-muted">
            Số tiền nạp (VND)
            <input type="number" value={amount} onChange={(e) => setAmount(Number(e.target.value))}
              className="mt-1 w-full rounded-md ui-divider border bg-transparent px-3 py-2 text-sm outline-none ui-text" />
          </label>
          <label className="block text-xs ui-muted">
            Tên chủ tài khoản chuyển tiền <span className="text-rose-500">*</span> — nên đứng tên công ty
            <input value={payerName} onChange={(e) => setPayerName(e.target.value)}
              placeholder={businessName}
              className="mt-1 w-full rounded-md ui-divider border bg-transparent px-3 py-2 text-sm outline-none ui-text" />
          </label>
          <div className="grid grid-cols-2 gap-2">
            <input value={payerAccountNo} onChange={(e) => setPayerAccountNo(e.target.value)} placeholder="Số tài khoản (tuỳ chọn)"
              className="rounded-md ui-divider border bg-transparent px-3 py-2 text-sm outline-none ui-text" />
            <input value={payerBank} onChange={(e) => setPayerBank(e.target.value)} placeholder="Ngân hàng (tuỳ chọn)"
              className="rounded-md ui-divider border bg-transparent px-3 py-2 text-sm outline-none ui-text" />
          </div>
          <p className="text-[11px] ui-muted">
            🔒 Để chống mạo danh, tiền phải chuyển từ tài khoản ngân hàng <b>đứng tên công ty</b>. Tên lệch sẽ bị kiểm tra kỹ trước khi cộng ví.
          </p>
          <div className="flex items-center gap-2">
            <button onClick={submit} disabled={busy}
              className="rounded-md bg-[#0071A6] text-white text-sm px-3 py-2 hover:brightness-110 disabled:opacity-50">
              {busy ? '…' : 'Tạo yêu cầu nạp'}
            </button>
            <button onClick={() => setOpen(false)} className="text-sm ui-muted">Huỷ</button>
          </div>
        </div>
      )}
      {instruction && (
        <div className="mt-2 rounded-md border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 p-3 text-sm">
          <div className="font-semibold text-emerald-700 dark:text-emerald-300 mb-1">Chuyển khoản để nạp {fmtVnd(instruction.amountVnd)}</div>
          {instruction.bankAccountNo ? (
            <>
              <div className="ui-text">Ngân hàng: <b>{instruction.bankName}</b></div>
              <div className="ui-text">Số TK: <b>{instruction.bankAccountNo}</b> · {instruction.bankAccountName}</div>
            </>
          ) : (
            <div className="text-rose-600">Cab chưa có số tài khoản nhận tiền. Bạn chưa chuyển khoản lúc này, vui lòng liên hệ Cab.</div>
          )}
          <div className="ui-text">Nội dung CK: <b className="text-rose-600">{instruction.transferNote}</b> (bắt buộc đúng để đối soát)</div>
          <div className="text-xs ui-muted mt-1">Sau khi chuyển khoản, kế toán xác nhận sẽ cộng vào ví (thường trong ngày).</div>
          <button onClick={() => { setOpen(false); setInstruction(null); }} className="mt-2 text-sm text-[#0071A6]">Xong</button>
        </div>
      )}
      {wallet && wallet.topUps.length > 0 && (
        <div className="mt-2 text-xs ui-muted space-y-0.5">
          {wallet.topUps.slice(0, 5).map((t) => (
            <div key={t.id} className="flex justify-between">
              <span>{fmtVnd(t.amountVnd)} · {t.transferNote}</span>
              <span className={t.status === 'Confirmed' ? 'text-emerald-600' : t.status === 'Rejected' ? 'text-rose-600' : 'text-amber-600'}>
                {t.status === 'Confirmed' ? 'Đã cộng' : t.status === 'Rejected' ? 'Từ chối' : 'Chờ xác nhận'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const STATUS: Record<string, { label: string; cls: string; Icon: any }> = {
  Pending: { label: 'Chờ duyệt', cls: 'bg-amber-100 text-amber-700', Icon: Clock },
  Verified: { label: 'Đã xác thực', cls: 'bg-emerald-100 text-emerald-700', Icon: CheckCircle2 },
  Rejected: { label: 'Bị từ chối', cls: 'bg-rose-100 text-rose-700', Icon: XCircle },
};

const emptyForm: CreateAdAccountPayload = {
  businessName: '',
  taxCode: '',
  businessLicenseNo: '',
  address: '',
  representativeName: '',
  contactEmail: '',
  contactPhone: '',
  licenseImageUrl: '',
  taxImageUrl: '',
  authorizationImageUrl: '',
};

type DocKey = 'licenseImageUrl' | 'taxImageUrl' | 'authorizationImageUrl';

const DocUpload: React.FC<{
  label: string;
  required?: boolean;
  value?: string;
  onChange: (url: string) => void;
}> = ({ label, required, value, onChange }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (file?: File) => {
    if (!file) return;
    try {
      setBusy(true);
      const fd = new FormData();
      fd.append('type', file);
      const res = await fetchUrlImages(fd);
      if (res?.length) onChange(res[0].url);
    } catch {
      toast.error('Tải ảnh thất bại.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="text-sm ui-text">
      {label} {required && <span className="text-rose-500">*</span>}
      <label className="mt-1 flex items-center gap-2 rounded-md ui-divider border px-3 py-2 cursor-pointer w-fit">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}
        {value ? 'Đổi ảnh' : 'Chọn ảnh'}
        <input type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files?.[0])} />
      </label>
      {value ? <img src={value} alt="" className="mt-2 h-16 rounded-md object-cover border ui-divider" /> : null}
    </div>
  );
};

/**
 * purpose: 'ads' (Cab Ads — Cab bán trọn dịch vụ quảng cáo nên xuất hoá đơn cả giá trị) hoặc 'collab' (thuê creator —
 * Cab chỉ thu phí sàn, phần còn lại là thu nhập của creator nên KHÔNG hứa hoá đơn VAT cả đơn; sếp góp ý 14/10).
 */
const BusinessAccountSection: React.FC<{ purpose?: 'ads' | 'collab' }> = ({ purpose = 'ads' }) => {
  const [accounts, setAccounts] = useState<AdAccount[]>([]);
  const [form, setForm] = useState<CreateAdAccountPayload>(emptyForm);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [taxCheck, setTaxCheck] = useState<TaxLookupResult | null>(null); // đối chiếu MST với cơ quan thuế
  const [ekyc, setEkyc] = useState<EkycStatus | null>(null); // định danh người đại diện (chống mạo danh)
  const [ekycOpen, setEkycOpen] = useState(false);
  const [adsConsent, setAdsConsent] = useState<boolean | null>(null); // đã đồng ý điều khoản Cab Ads DN
  const [policyOpen, setPolicyOpen] = useState(false);

  // Tài khoản cá nhân có thẻ riêng (IndividualOrdererCard), không hiện ở đây cùng ví VND/giấy tờ.
  const load = () =>
    getMyAdAccounts()
      .then((accs) => setAccounts(accs.filter((a) => !isIndividualAccount(a))))
      .catch(() => undefined);
  const loadEkyc = () => getEkycStatus().then(setEkyc).catch(() => undefined);
  const loadConsent = () =>
    getConsentStatus(['ads-business'])
      .then((list) => setAdsConsent(!!list.find((x) => x.key === 'ads-business')?.accepted))
      .catch(() => undefined);
  useEffect(() => {
    load();
    loadEkyc();
    loadConsent();
  }, []);

  // Bấm tạo tài khoản DN → theo thứ tự: đồng ý điều khoản → định danh eKYC → mở form.
  const onCreateClick = () => {
    if (!adsConsent) {
      setPolicyOpen(true);
      return;
    }
    if (!ekyc?.isVerified) {
      setEkycOpen(true);
      toast('Bạn cần định danh (eKYC) người đại diện trước khi mở tài khoản doanh nghiệp.', { icon: '🪪' });
      return;
    }
    setOpen(true);
  };

  // Tra cứu MST (debounce) khi đủ 10/13 chữ số → hiện tên DN theo cơ quan thuế để đối chiếu.
  const mstDigits = (form.taxCode || '').replace(/\D/g, '');
  useEffect(() => {
    if (mstDigits.length !== 10 && mstDigits.length !== 13) {
      setTaxCheck(null);
      return;
    }
    const t = setTimeout(() => {
      lookupTaxCode(form.taxCode).then(setTaxCheck).catch(() => setTaxCheck(null));
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mstDigits]);

  const set = (patch: Partial<CreateAdAccountPayload>) => setForm((p) => ({ ...p, ...patch }));

  const submit = async () => {
    if (!form.businessName.trim()) return toast.error('Nhập tên doanh nghiệp.');
    if (!form.taxCode.trim()) return toast.error('Nhập mã số thuế (MST).');
    if (!form.licenseImageUrl) return toast.error('Tải ảnh Giấy phép kinh doanh.');
    try {
      setSubmitting(true);
      await createAdAccount(form);
      toast.success('Đã gửi hồ sơ. Chờ duyệt để bắt đầu chạy quảng cáo doanh nghiệp.');
      setForm(emptyForm);
      setOpen(false);
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Gửi hồ sơ thất bại.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-xl ui-divider border p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold ui-text flex items-center gap-2">
          <Building2 size={18} className="text-[#0071A6]" /> Tài khoản doanh nghiệp
        </h2>
        {!open && (
          <button
            onClick={onCreateClick}
            className="rounded-md bg-[#0071A6] text-white text-sm px-3 py-1.5 hover:brightness-110"
          >
            + Tạo tài khoản doanh nghiệp
          </button>
        )}
      </div>
      <p className="text-xs ui-muted -mt-1">
        {purpose === 'collab'
          ? 'Thuê creator với tư cách doanh nghiệp, tiền ký quỹ lấy từ ví doanh nghiệp. Cần nộp giấy tờ để xác thực.'
          : 'Chạy quảng cáo với tư cách doanh nghiệp, dùng ngân sách lớn từ ví doanh nghiệp. Cần nộp giấy tờ để xác thực.'}
      </p>

      {/* Trạng thái định danh người đại diện (eKYC) — chống mạo danh */}
      {ekyc &&
        (ekyc.isVerified ? (
          <div className="flex items-center gap-2 rounded-md bg-emerald-50 dark:bg-emerald-950/30 px-3 py-2 text-xs text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 size={14} /> Người đại diện đã định danh{ekyc.verifiedName ? `: ${ekyc.verifiedName}` : ''}
          </div>
        ) : (
          <div className="flex items-center justify-between gap-2 rounded-md bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
            <span>🪪 Chưa định danh người đại diện — bắt buộc để mở tài khoản doanh nghiệp.</span>
            <button onClick={() => setEkycOpen(true)} className="rounded bg-amber-600 text-white px-2 py-1 hover:brightness-110 shrink-0">
              Định danh ngay
            </button>
          </div>
        ))}

      {/* Modal eKYC (chỉ định danh, không thu thông tin ngân hàng) */}
      <EkycCameraModal
        open={ekycOpen}
        identityOnly
        onClose={() => setEkycOpen(false)}
        onVerified={() => {
          setEkycOpen(false);
          loadEkyc();
          toast.success('Định danh thành công! Bạn có thể tạo tài khoản doanh nghiệp.');
          if (adsConsent) setOpen(true);
        }}
      />

      {/* Modal điều khoản Cab Ads Doanh nghiệp — bấm đồng ý trước khi dùng */}
      <PolicyConsentModal
        open={policyOpen}
        policyKey="ads-business"
        onClose={() => setPolicyOpen(false)}
        onAccepted={() => {
          setPolicyOpen(false);
          setAdsConsent(true);
          toast.success('Đã đồng ý điều khoản.');
          // Sau khi đồng ý điều khoản: nếu chưa eKYC thì sang bước định danh, xong thì mở form.
          if (ekyc?.isVerified) setOpen(true);
          else setEkycOpen(true);
        }}
      />

      {/* Danh sách tài khoản DN của tôi */}
      {accounts.length > 0 && (
        <div className="space-y-2">
          {accounts.map((a) => {
            const st = STATUS[a.status] || { label: a.status, cls: 'bg-slate-200 text-slate-700', Icon: Clock };
            return (
              <div key={a.id} className="rounded-lg ui-divider border p-3">
                <div className="flex items-center gap-2">
                  <span className="font-medium ui-text truncate">{a.businessName}</span>
                  <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${st.cls}`}>
                    <st.Icon size={12} /> {st.label}
                  </span>
                </div>
                <div className="text-xs ui-muted">MST: {a.taxCode}{a.businessLicenseNo ? ` · GPKD: ${a.businessLicenseNo}` : ''}</div>
                {a.status === 'Rejected' && a.rejectReason ? (
                  <div className="text-xs text-rose-600">Lý do từ chối: {a.rejectReason}</div>
                ) : null}
                {a.status === 'Pending' ? (
                  <div className="text-xs text-amber-600">Đang chờ admin duyệt hồ sơ.</div>
                ) : null}
                {a.status === 'Verified' ? <BusinessWalletBox accountId={a.id} businessName={a.businessName} /> : null}
              </div>
            );
          })}
        </div>
      )}

      {/* Form tạo */}
      {open && (
        <div className="rounded-lg bg-slate-50 dark:bg-slate-800/50 p-3 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="text-sm ui-text">
              Tên doanh nghiệp <span className="text-rose-500">*</span>
              <input className="mt-1 w-full rounded-md ui-divider border bg-transparent px-3 py-2 text-sm outline-none"
                value={form.businessName} onChange={(e) => set({ businessName: e.target.value })} placeholder="Công ty TNHH ..." />
            </label>
            <label className="text-sm ui-text">
              Mã số thuế (MST) <span className="text-rose-500">*</span>
              <input className="mt-1 w-full rounded-md ui-divider border bg-transparent px-3 py-2 text-sm outline-none"
                value={form.taxCode} onChange={(e) => set({ taxCode: e.target.value })} placeholder="03xxxxxxxx" />
              {taxCheck && taxCheck.lookupEnabled && taxCheck.found && (
                <span className="mt-1 flex items-start gap-1 text-[11px] text-emerald-600 dark:text-emerald-400">
                  <CheckCircle2 size={13} className="mt-px shrink-0" />
                  <span>Theo cơ quan thuế: <b>{taxCheck.name}</b>{taxCheck.address ? ` · ${taxCheck.address}` : ''}. Hãy điền đúng tên này.</span>
                </span>
              )}
              {taxCheck && taxCheck.lookupEnabled && !taxCheck.found && (
                <span className="mt-1 flex items-center gap-1 text-[11px] text-rose-600">
                  <XCircle size={13} /> Không tìm thấy doanh nghiệp với MST này. Kiểm tra lại.
                </span>
              )}
            </label>
            <label className="text-sm ui-text">
              Số giấy phép KD
              <input className="mt-1 w-full rounded-md ui-divider border bg-transparent px-3 py-2 text-sm outline-none"
                value={form.businessLicenseNo || ''} onChange={(e) => set({ businessLicenseNo: e.target.value })} />
            </label>
            <label className="text-sm ui-text">
              Người đại diện
              <input className="mt-1 w-full rounded-md ui-divider border bg-transparent px-3 py-2 text-sm outline-none"
                value={form.representativeName || ''} onChange={(e) => set({ representativeName: e.target.value })} />
            </label>
            <label className="text-sm ui-text">
              Email kế toán
              <input className="mt-1 w-full rounded-md ui-divider border bg-transparent px-3 py-2 text-sm outline-none"
                value={form.contactEmail || ''} onChange={(e) => set({ contactEmail: e.target.value })} />
            </label>
            <label className="text-sm ui-text">
              Điện thoại
              <input className="mt-1 w-full rounded-md ui-divider border bg-transparent px-3 py-2 text-sm outline-none"
                value={form.contactPhone || ''} onChange={(e) => set({ contactPhone: e.target.value })} />
            </label>
          </div>
          <label className="text-sm ui-text block">
            Địa chỉ
            <input className="mt-1 w-full rounded-md ui-divider border bg-transparent px-3 py-2 text-sm outline-none"
              value={form.address || ''} onChange={(e) => set({ address: e.target.value })} />
          </label>

          <div className="grid sm:grid-cols-3 gap-3">
            <DocUpload label="Giấy phép KD" required value={form.licenseImageUrl}
              onChange={(url) => set({ licenseImageUrl: url })} />
            <DocUpload label="Xác nhận MST" value={form.taxImageUrl}
              onChange={(url) => set({ taxImageUrl: url })} />
            <DocUpload label="Giấy ủy quyền" value={form.authorizationImageUrl}
              onChange={(url) => set({ authorizationImageUrl: url })} />
          </div>

          <div className="flex gap-2">
            <button onClick={submit} disabled={submitting}
              className="rounded-md bg-[#0071A6] text-white text-sm px-4 py-2 hover:brightness-110 disabled:opacity-50">
              {submitting ? 'Đang gửi…' : 'Gửi hồ sơ xác thực'}
            </button>
            <button onClick={() => { setOpen(false); setForm(emptyForm); }}
              className="rounded-md ui-divider border text-sm px-4 py-2 ui-text hover:bg-black/5 dark:hover:bg-white/5">
              Huỷ
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessAccountSection;
